import { FC, MutableRefObject, Suspense, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useMutation } from 'react-relay';
import { ArrowLeft, ArrowRight, Presentation, UserRoundPlus, Users } from 'lucide-react';
import { addUsersToClass, createClass } from '../../../graphQLOperations/Classes';
import { useSaveClassIcon } from '../../../custom-hooks/useSaveClassIcon';
import { ClassesCreateClassMutation } from '../../../graphQLOperations/__generated__/ClassesCreateClassMutation.graphql';
import { ClassesAddUsersToClassMutation } from '../../../graphQLOperations/__generated__/ClassesAddUsersToClassMutation.graphql';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { noteClassesChanged } from '../../../redux/reducers/globalClassFilterSlice';
import { selectUser } from '../../../redux/reducers/userSlice';
import { Roles } from '../../../models/User';
import { Class } from '../../../models/Class';
import { classNameSectionExists } from '../../../businessLogicService/classNameSectionExists';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import ClassWizardDetailsStep from './ClassWizardDetailsStep';
import ClassWizardCoTeacherStep from './ClassWizardCoTeacherStep';
import ClassWizardStudentsStep from './ClassWizardStudentsStep';
import ClassWizardNewStudentDialog from './ClassWizardNewStudentDialog';
import ClassWizardNewTeacherDialog from './ClassWizardNewTeacherDialog';
import { ClassWizardData, emptyWizardData, isDetailsStepValid, WizardUser } from './wizardTypes';

interface Props {
  visible: boolean;
  onHide: () => void;
  onCreated: (created: Class) => void;
  toast: MutableRefObject<Toast | null>;
  /** Classes already visible to the user, used to validate name + section uniqueness. */
  existingClasses: Class[];
}

type Step = 1 | 2 | 3;

const STEP_META: Record<Step, { title: string; subtitle: string; optional?: boolean; icon: JSX.Element }> = {
  1: {
    title: 'Create new class',
    subtitle: 'Complete the required fields to create your class.',
    icon: <Presentation size={17} aria-hidden />,
  },
  2: {
    title: 'Add co-teacher',
    subtitle:
      "Co-teachers can view and edit class details and rosters, and will have access to all class reports. Admins don't need to be added as co-teachers because they can see all classes at their site.",
    optional: true,
    icon: <UserRoundPlus size={17} aria-hidden />,
  },
  3: {
    title: 'Add students',
    subtitle: 'Select from the list or add a new student. You can also remove or add additional students later.',
    optional: true,
    icon: <Users size={17} aria-hidden />,
  },
};

const STEP_WIDTH: Record<Step, string> = { 1: '32.5rem', 2: '38.75rem', 3: '38.75rem' };
const STEP_MIN_HEIGHT: Record<Step, string> = { 1: '31.25rem', 2: '36.0625rem', 3: '37rem' };

const ClassesRosterCreateWizard: FC<Props> = ({ visible, onHide, onCreated, toast, existingClasses }) => {
  const userRedux = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<Step>(1);
  const [data, setData] = useState<ClassWizardData>(emptyWizardData);
  const [loading, setLoading] = useState(false);
  const [duplicateName, setDuplicateName] = useState(false);
  const [newStudentVisible, setNewStudentVisible] = useState(false);
  const [newTeacherVisible, setNewTeacherVisible] = useState(false);
  const [extraTeachers, setExtraTeachers] = useState<WizardUser[]>([]);

  const [createClassMutation] = useMutation<ClassesCreateClassMutation>(createClass);
  const saveClassIcon = useSaveClassIcon();
  const [addUsersMutation] = useMutation<ClassesAddUsersToClassMutation>(addUsersToClass);

  // Editing any of name/section/site clears a previous duplicate warning.
  const patch = (part: Partial<ClassWizardData>) => {
    if ('name' in part || 'section' in part || 'site' in part) setDuplicateName(false);
    setData(prev => ({ ...prev, ...part }));
  };

  const reset = () => {
    setData(emptyWizardData);
    setStep(1);
    setLoading(false);
    setDuplicateName(false);
    setNewStudentVisible(false);
    setNewTeacherVisible(false);
  };

  const goToCoTeacherStep = () => {
    if (classNameSectionExists(existingClasses, data.name, data.section, data.site?.id)) {
      setDuplicateName(true);
      return;
    }
    setStep(2);
  };

  const close = () => {
    reset();
    onHide();
  };

  const showError = (message?: string) => {
    setLoading(false);
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message ?? 'Something went wrong. Please try again.',
    });
  };

  const finishOptimistically = (createdId: string, coTeachers: WizardUser[], students: WizardUser[]) => {
    const asUser = (user: WizardUser, role: Roles) => ({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      roles: [{ name: role }],
    });
    const optimistic = {
      id: createdId,
      name: data.name,
      section: data.section,
      gradeLevels: data.grades,
      site: data.site ?? undefined,
      isActive: true,
      owner: { id: `UserNode:${userRedux?.id}`, firstName: userRedux?.name ?? '', lastName: '' },
      users: [...coTeachers.map(t => asUser(t, Roles.TEACHER)), ...students.map(s => asUser(s, Roles.STUDENT))],
    } as unknown as Class;

    toast.current?.show({ severity: 'success', detail: `"${data.name}" created successfully!` });
    onCreated(optimistic);
    dispatch(noteClassesChanged());
    close();
  };

  const submit = (includeStudents: boolean) => {
    if (loading || !userRedux) return;
    setLoading(true);

    const coTeachers = data.coTeachers;
    const students = includeStudents ? data.students : [];

    createClassMutation({
      variables: {
        input: {
          siteId: data.site?.id ?? '',
          ownerId: `UserNode:${userRedux.id}`,
          name: data.name,
          section: data.section,
          isJoinCodeActive: true,
          isActive: true,
          gradeIds: data.grades.map(grade => grade.id),
        },
      },
      onCompleted: response => {
        const createdId = response.createClass?.classNode?.id;
        if (!createdId) {
          showError(response.createClass?.errors?.[0]?.message);
          return;
        }
        saveClassIcon(createdId, data.icon);
        const userIds = [...coTeachers, ...students].map(user => user.id);
        if (!userIds.length) {
          finishOptimistically(createdId, coTeachers, students);
          return;
        }
        addUsersMutation({
          variables: { input: { userIds, classId: createdId } },
          onCompleted: () => finishOptimistically(createdId, coTeachers, students),
          onError: () => finishOptimistically(createdId, coTeachers, students),
        });
      },
      onError: () => showError(),
    });
  };

  const meta = STEP_META[step];

  const header = (
    <div className="flex align-items-center gap-md">
      <span className="nc_wizard-header-icon">{meta.icon}</span>

      <div className="flex flex-column">
        <span className="flex align-items-center gap-sm line-height-1">
          <h2 className="text-xl font-semibold line-height-1 m-0 tw-not-italic">{meta.title}</h2>

          {meta.optional && <span className="nc_wizard-optional">Optional</span>}
        </span>

        <span className="text-color-secondary text-sm tw-leading-5 mt-1 tw-font-normal">{meta.subtitle}</span>
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-content-between align-items-center w-full">
      {step === 1 ? (
        <Button label="Cancel" text onClick={close} disabled={loading} />
      ) : (
        <Button
          label="Back"
          icon={<ArrowLeft size={16} />}
          text
          style={{ gap: '0.5rem' }}
          onClick={() => setStep((step - 1) as Step)}
          disabled={loading}
        />
      )}
      <div className="flex gap-md">
        {step === 2 && (
          <Button label="Skip & Add students" outlined rounded onClick={() => setStep(3)} disabled={loading} />
        )}
        {step === 3 && (
          <Button label="Skip & create class" outlined rounded onClick={() => submit(false)} loading={loading} />
        )}
        {step === 1 && (
          <Button
            label="Next: Add Co-teacher"
            icon={<ArrowRight size={18} />}
            iconPos="right"
            rounded
            className="nc_wizard-icon-right"
            style={{ gap: '0.75rem' }}
            disabled={!isDetailsStepValid(data)}
            onClick={goToCoTeacherStep}
          />
        )}
        {step === 2 && (
          <Button
            label="Next: Add students"
            icon={<ArrowRight size={18} />}
            iconPos="right"
            rounded
            className="nc_wizard-icon-right"
            style={{ gap: '0.75rem' }}
            onClick={() => setStep(3)}
            disabled={loading || !data.coTeachers.length}
          />
        )}
        {step === 3 && (
          <Button
            label="Add & create class"
            rounded
            onClick={() => submit(true)}
            loading={loading}
            disabled={!data.students.length}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        header={header}
        footer={footer}
        visible={visible}
        onHide={close}
        className="nc_class-wizard"
        style={{ width: STEP_WIDTH[step], minHeight: STEP_MIN_HEIGHT[step] }}
        dismissableMask={false}
        ariaCloseIconLabel="Close"
      >
        <Suspense fallback={<ThreeDotLoader dark />}>
          {step === 1 && <ClassWizardDetailsStep data={data} onChange={patch} duplicateName={duplicateName} />}
          {step === 2 && (
            <ClassWizardCoTeacherStep
              siteId={data.site?.id ?? ''}
              selected={data.coTeachers}
              onChange={coTeachers => patch({ coTeachers })}
              onNewTeacher={() => setNewTeacherVisible(true)}
              extraTeachers={extraTeachers}
            />
          )}
          {step === 3 && (
            <ClassWizardStudentsStep
              siteId={data.site?.id ?? ''}
              selected={data.students}
              onChange={students => patch({ students })}
              onNewStudent={() => setNewStudentVisible(true)}
            />
          )}
        </Suspense>
      </Dialog>
      <ClassWizardNewStudentDialog
        visible={newStudentVisible}
        onHide={() => setNewStudentVisible(false)}
        siteId={data.site?.id ?? ''}
        siteName={data.site?.name ?? ''}
        toast={toast}
        onCreated={student => {
          patch({ students: [...data.students, student] });
          setNewStudentVisible(false);
        }}
      />
      <ClassWizardNewTeacherDialog
        visible={newTeacherVisible}
        onHide={() => setNewTeacherVisible(false)}
        siteId={data.site?.id ?? ''}
        siteName={data.site?.name ?? ''}
        toast={toast}
        onCreated={teacher => {
          patch({ coTeachers: [...data.coTeachers, teacher] });
          setExtraTeachers(prev => [...prev, teacher]);
          setNewTeacherVisible(false);
        }}
      />
    </>
  );
};

export default ClassesRosterCreateWizard;
